import { useNavigate } from "@tanstack/react-router";
import type { CreatorTriggerType } from "@unbox/api";
import {
  AlertTriangle,
  ClockStopwatch,
  Dataflow03,
  DotsVertical,
  Edit03,
  MagicWand01,
  Plus,
  Power01,
  Send01,
  Trash02,
  Zap,
} from "@untitledui/icons";
import { useState } from "react";
import {
  useDeleteAutomation,
  useSaveSequenceTriggerBinding,
  useSequenceTriggerBinding,
  useSequenceTriggers,
} from "@/api/sequences";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";
import { AutomateOutreachEmptyState } from "./automate-outreach-empty-state";
import { TRIGGER_ICON } from "./automation-builder";

/**
 * One saved automation. The catalogue row carries the trigger and its override count, but the
 * step count and the customized/excluded split only exist on the binding detail, so each row
 * fetches its own.
 */
function AutomationRow({
  triggerType,
  label,
  enabled,
  onNavigate,
}: {
  triggerType: CreatorTriggerType;
  label: string;
  enabled: boolean;
  onNavigate: () => void;
}) {
  const { data: binding } = useSequenceTriggerBinding(triggerType);
  const saveBinding = useSaveSequenceTriggerBinding();
  const deleteAutomation = useDeleteAutomation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const Icon = TRIGGER_ICON[triggerType];

  const customized = new Set((binding?.overrides ?? []).filter((o) => o.enabled && o.sequence).map((o) => o.cohort?.id))
    .size;
  const excluded = (binding?.overrides ?? []).filter((o) => !o.enabled).length;

  const summary = [
    pluralize(customized, "cohort customized", "cohorts customized"),
    pluralize(excluded, "cohort excluded", "cohorts excluded"),
  ].join(" · ");

  const setEnabled = async (next: boolean) => {
    if (!binding) return;
    try {
      await saveBinding.mutateAsync({
        triggerType,
        defaultSequenceId: binding.defaultSequence?.id ?? null,
        enabled: next,
        triggerConfig: binding.triggerConfig,
        overrides: binding.overrides
          .filter((o) => o.cohort)
          .map((o) => ({
            cohortId: o.cohort!.id,
            enabled: o.enabled,
            sequenceId: o.sequence?.id ?? null,
            triggerConfig: o.triggerConfig,
          })),
      });
      toastSuccess(next ? "Automation turned on" : "Automation turned off");
    } catch (e) {
      toastError("Couldn't update automation", { description: e instanceof Error ? e.message : undefined });
    }
  };

  const remove = async () => {
    try {
      await deleteAutomation.mutateAsync(triggerType);
      setConfirmDelete(false);
      toastSuccess("Automation deleted", { description: `${label} is no longer set up.` });
    } catch (e) {
      toastError("Couldn't delete automation", { description: e instanceof Error ? e.message : undefined });
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onNavigate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onNavigate();
      }}
      className="flex cursor-pointer items-center gap-4 rounded-xl border border-secondary bg-primary px-5 py-4 shadow-xs transition-all duration-100 hover:border-gray-300 hover:shadow-sm"
    >
      <span
        aria-hidden
        className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-unbox-cream-50 text-secondary"
      >
        <Icon className="size-[22px]" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-semibold text-primary">{label}</span>
        <span className="truncate text-[13px] text-tertiary">{summary}</span>
      </div>

      {enabled ? (
        <BadgeWithDot color="success" size="sm">
          Active
        </BadgeWithDot>
      ) : (
        <BadgeWithDot color="gray" size="sm">
          Off
        </BadgeWithDot>
      )}

      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Dropdown.DotsButton aria-label={`Actions for ${label}`} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onNavigate}>
              <Edit03 className="mr-2 size-4" />
              Edit automation
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setEnabled(!enabled)}>
              <Power01 className="mr-2 size-4" />
              {enabled ? "Turn off automation" : "Turn on automation"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-error-primary" onSelect={() => setConfirmDelete(true)}>
              <Trash02 className="mr-2 size-4" />
              Delete automation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <DialogContent className="sm:max-w-[480px]" hideCloseButton>
            <DialogHeader className="items-center space-y-2 text-center sm:text-center">
              <AlertTriangle className="mb-2 size-7 text-utility-warning-500" />
              <DialogTitle>Delete this automation?</DialogTitle>
              <DialogDescription className="text-md">
                {label} will stop running, and creators part-way through it will still receive their remaining messages.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4 flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end sm:space-x-0">
              <Button color="secondary" size="sm" onClick={() => setConfirmDelete(false)}>
                Keep automation
              </Button>
              <Button color="primary-destructive" size="sm" isLoading={deleteAutomation.isPending} onClick={remove}>
                Delete automation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </span>
    </div>
  );
}

export function Sequences() {
  const navigate = useNavigate();
  const merchantId = useMerchant();
  const { data: triggers = [], isLoading } = useSequenceTriggers();

  const configured = triggers.filter((t) => t.defaultSequence !== null || t.cohortOverrideCount > 0);

  const openBuilder = (triggerType: string, fromEmptyState?: boolean) =>
    navigate({
      to: "/merchant/$merchantId/automate-outreach/automation/$triggerType",
      params: { merchantId, triggerType },
      search: fromEmptyState ? { fromEmptyState } : {},
    });

  if (isLoading) return <LoadingPageState />;

  // First run: the full-bleed dark empty state replaces the whole page body.
  if (configured.length === 0) {
    return (
      <AutomateOutreachEmptyState
        onBuild={() => openBuilder("new", true)}
        onStartFromTemplate={() => openBuilder("new", true)}
      />
    );
  }

  return (
    <PageLayout
      title="Automate outreach"
      icon={Send01}
      subtitle="Configure multi-step message flows, that can be auto triggered"
      callToAction={
        <div className="flex shrink-0 items-center gap-3">
          <Button
            color="secondary"
            iconLeading={<Dataflow03 className="size-4" />}
            onClick={() => navigate({ to: "/merchant/$merchantId/automate-outreach/runs", params: { merchantId } })}
          >
            View Runs
          </Button>
          <Button iconLeading={<Plus className="size-5" />} onClick={() => openBuilder("new")}>
            Create New Automation
          </Button>
        </div>
      }
    >
      <PageHero
        pageKey={PageHeroKey.AutomateOutreach}
        title="Start a sequence the moment a creator acts."
        text1={{ icon: Zap, message: "Choose the creator trigger that starts a sequence." }}
        text2={{ icon: MagicWand01, message: "Personalise the messages with photos and variables." }}
        text3={{ icon: ClockStopwatch, message: "Let the sequence follow up so you don't have to." }}
        image="/heroes/automate-outreach.png"
      />

      <section className="flex shrink-0 flex-col gap-[30px]">
        <h2 className="text-xl leading-[30px] text-primary">Your Automations</h2>
        <div className="flex flex-col gap-3">
          {configured.map((trigger) => (
            <AutomationRow
              key={trigger.type}
              triggerType={trigger.type}
              label={trigger.label}
              enabled={trigger.enabled}
              onNavigate={() => openBuilder(trigger.type)}
            />
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
